import React, { useState, useEffect } from "react";
import { Globe, Check } from "lucide-react";
import { Card } from "flowbite-react";
import { useTranslation } from "react-i18next";

export type PrayerSource = "islamiska" | "vaktija" | "aladhan";

interface PrayerSourceInfo {
    id: PrayerSource;
    nameKey: string;
    descriptionKey: string;
    flag: string;
}

const sources: PrayerSourceInfo[] = [
    {
        id: "islamiska",
        nameKey: "sourceIslamiska",
        descriptionKey: "sourceIslamiskDesc",
        flag: "🇸🇪",
    },
    {
        id: "vaktija",
        nameKey: "sourceVaktija",
        descriptionKey: "sourceVaktijaDesc",
        flag: "🇧🇦",
    },
    {
        id: "aladhan",
        nameKey: "sourceAladhan",
        descriptionKey: "sourceAladhanDesc",
        flag: "🌐",
    },
];

export function getPrayerSource(): PrayerSource {
    const saved = localStorage.getItem("prayerSource");
    if (saved === "islamiska" || saved === "vaktija" || saved === "aladhan") {
        return saved;
    }
    return "islamiska"; // default
}

export function getPrayerSourceLabel(source: PrayerSource): string {
    switch (source) {
        case "islamiska":
            return "Islamiska Förbundet";
        case "vaktija":
            return "Vaktija (Islamiska Gemenskapen)";
        case "aladhan":
            return "Aladhan API (Beräknad)";
    }
}

interface PrayerSourceSelectorProps {
    currentSource: PrayerSource;
    onSourceChange: (source: PrayerSource) => void;
}

const PrayerSourceSelector = ({
    currentSource,
    onSourceChange,
}: PrayerSourceSelectorProps) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState<PrayerSource>(currentSource);

    useEffect(() => {
        setSelected(currentSource);
    }, [currentSource]);

    const handleSelect = (source: PrayerSource) => {
        setSelected(source);
        localStorage.setItem("prayerSource", source);
        onSourceChange(source);
    };

    return (
        <Card className="w-full max-w-md mx-auto overflow-hidden animate-fadeIn shadow-lg">
            <div className="bg-purple-600 text-white p-4 rounded-t-lg">
                <div className="flex items-center justify-center gap-2 mb-2">
                    <Globe className="h-5 w-5 text-purple-200" />
                    <h2 className="text-xl font-bold">{t("prayerSource")}</h2>
                </div>
            </div>

            <div className="p-4 bg-gradient-to-b from-white to-purple-50 dark:from-gray-800 dark:to-purple-950/30 space-y-3">
                {sources.map((source) => {
                    const isSelected = selected === source.id;
                    return (
                        <button
                            key={source.id}
                            onClick={() => handleSelect(source.id)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-200 text-left ${isSelected
                                    ? "border-purple-500 bg-purple-50 dark:bg-purple-950/40 shadow-md"
                                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-sm"
                                }`}
                        >
                            {/* Flag/Icon */}
                            <span className="text-2xl flex-shrink-0">{source.flag}</span>

                            {/* Text content */}
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-900 dark:text-gray-100">
                                    {t(source.nameKey)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {t(source.descriptionKey)}
                                </div>
                            </div>

                            {/* Checkmark */}
                            <div
                                className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                                        ? "bg-purple-600 border-purple-600"
                                        : "border-gray-300 dark:border-gray-600"
                                    }`}
                            >
                                {isSelected && <Check className="h-4 w-4 text-white" />}
                            </div>
                        </button>
                    );
                })}

                <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    {t("prayerSourceHint")}
                </div>
            </div>
        </Card>
    );
};

export default PrayerSourceSelector;
